// ==================== IMPORT CONTACTS MODAL ====================

import { useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Upload,
  FileSpreadsheet,
  ArrowRight,
  ArrowLeft,
  Check,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Download,
  Phone,
  User,
  Mail,
  Building,
  Globe,
  Tag,
  FileText,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import { Modal, Button, Checkbox, Select, Badge, Card } from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { contactsService, type ImportContactsData } from '../../../services/contacts.service';
import type { ContactGroup, ImportResult } from '../../../types';
import { normalizeCountryToCode } from '../../../constants/countries.constants';

// ==================== TYPES ====================

interface ParsedContact {
  [key: string]: string;
}

interface FieldMapping {
  csvColumn: string;
  contactField: string;
}

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ImportContactsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  groups: ContactGroup[];
}

// ==================== CONSTANTS ====================

const CONTACT_FIELDS: SelectOption[] = [
  { value: '', label: '-- Skip this column --' },
  { value: 'phoneNumber', label: 'Phone Number', icon: <Phone className="w-4 h-4" /> },
  { value: 'name', label: 'Name', icon: <User className="w-4 h-4" /> },
  { value: 'email', label: 'Email', icon: <Mail className="w-4 h-4" /> },
  { value: 'company', label: 'Company', icon: <Building className="w-4 h-4" /> },
  { value: 'country', label: 'Country Code', icon: <Globe className="w-4 h-4" /> },
  { value: 'tags', label: 'Tags (comma separated)', icon: <Tag className="w-4 h-4" /> },
  { value: 'notes', label: 'Notes', icon: <FileText className="w-4 h-4" /> },
];

const STEPS = [
  { id: 1, label: 'Upload', icon: Upload },
  { id: 2, label: 'Mapping', icon: RefreshCw },
  { id: 3, label: 'Preview', icon: FileSpreadsheet },
  { id: 4, label: 'Results', icon: CheckCircle },
];

// ==================== HELPER FUNCTIONS ====================

const parseCSV = (content: string): { headers: string[]; rows: ParsedContact[] } => {
  const lines = content.split(/\r?\n/).filter((line) => line.trim());
  if (lines.length === 0) return { headers: [], rows: [] };

  // Parse headers
  const headers = parseCSVLine(lines[0]);
  
  // Parse rows
  const rows: ParsedContact[] = [];
  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i]);
    if (values.some((v) => v.trim())) {
      const row: ParsedContact = {};
      headers.forEach((header, index) => {
        row[header] = values[index] || '';
      });
      rows.push(row);
    }
  }

  return { headers, rows };
};

const parseCSVLine = (line: string): string[] => {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (char === '"' && inQuotes && nextChar === '"') {
      current += '"';
      i++;
    } else if (char === '"') {
      inQuotes = !inQuotes;
    } else if (char === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }
  result.push(current.trim());

  return result;
};

const autoDetectMapping = (headers: string[]): FieldMapping[] => {
  const mappings: FieldMapping[] = [];
  
  const fieldPatterns: { field: string; patterns: RegExp[] }[] = [
    { field: 'phoneNumber', patterns: [/phone/i, /mobile/i, /cell/i, /tel/i, /number/i, /whatsapp/i] },
    { field: 'name', patterns: [/^name$/i, /full.?name/i, /contact.?name/i, /customer.?name/i] },
    { field: 'email', patterns: [/email/i, /e-mail/i, /mail/i] },
    { field: 'company', patterns: [/company/i, /organization/i, /org/i, /business/i, /firm/i] },
    { field: 'country', patterns: [/country/i, /location/i, /region/i] },
    { field: 'tags', patterns: [/tags?/i, /labels?/i, /categories?/i] },
    { field: 'notes', patterns: [/notes?/i, /comments?/i, /description/i, /remarks?/i] },
  ];

  headers.forEach((header) => {
    let matchedField = '';
    
    for (const { field, patterns } of fieldPatterns) {
      if (patterns.some((p) => p.test(header))) {
        // Check if this field is already mapped
        if (!mappings.some((m) => m.contactField === field)) {
          matchedField = field;
          break;
        }
      }
    }

    mappings.push({
      csvColumn: header,
      contactField: matchedField,
    });
  });

  return mappings;
};

const validateContact = (
  contact: ParsedContact,
  mapping: FieldMapping[],
  rowIndex: number
): ValidationError[] => {
  const errors: ValidationError[] = [];
  
  // Get phone number from mapping
  const phoneMapping = mapping.find((m) => m.contactField === 'phoneNumber');
  const phoneValue = phoneMapping ? contact[phoneMapping.csvColumn] : '';
  
  if (!phoneValue || !phoneValue.trim()) {
    errors.push({
      row: rowIndex + 1,
      field: 'phoneNumber',
      message: 'Phone number is required',
    });
  } else {
    // Basic phone validation - just numbers and optional + prefix
    const cleanPhone = phoneValue.replace(/[\s\-()]/g, '');
    if (!/^\+?\d{7,15}$/.test(cleanPhone)) {
      errors.push({
        row: rowIndex + 1,
        field: 'phoneNumber',
        message: 'Invalid phone number format',
      });
    }
  }

  // Validate email if mapped
  const emailMapping = mapping.find((m) => m.contactField === 'email');
  if (emailMapping) {
    const emailValue = contact[emailMapping.csvColumn];
    if (emailValue && emailValue.trim()) {
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailValue)) {
        errors.push({
          row: rowIndex + 1,
          field: 'email',
          message: 'Invalid email format',
        });
      }
    }
  }

  // Validate country if mapped - warn if not recognized (but don't block import)
  const countryMapping = mapping.find((m) => m.contactField === 'country');
  if (countryMapping) {
    const countryValue = contact[countryMapping.csvColumn];
    if (countryValue && countryValue.trim()) {
      const normalizedCode = normalizeCountryToCode(countryValue);
      if (!normalizedCode) {
        errors.push({
          row: rowIndex + 1,
          field: 'country',
          message: `Unrecognized country: "${countryValue}"`,
        });
      }
    }
  }

  return errors;
};

const mapContactData = (
  contact: ParsedContact,
  mapping: FieldMapping[]
): ImportContactsData['contacts'][0] | null => {
  const result: Partial<ImportContactsData['contacts'][0]> = {};

  mapping.forEach(({ csvColumn, contactField }) => {
    if (!contactField) return;
    
    const value = contact[csvColumn]?.trim() || '';
    if (!value) return;

    if (contactField === 'tags') {
      result.tags = value.split(/[,;]/).map((t) => t.trim()).filter(Boolean);
    } else if (contactField === 'phoneNumber') {
      // Clean phone number - remove spaces, dashes, parentheses
      const cleanedPhone = value.replace(/[\s\-()]/g, '');
      // Ensure it starts with + for international format
      result.phoneNumber = cleanedPhone.startsWith('+') ? cleanedPhone : cleanedPhone;
    } else if (contactField === 'country') {
      // Normalize country input to ISO code
      // Handles: "USA", "US", "United States", "uk", "UAE", etc.
      const normalizedCode = normalizeCountryToCode(value);
      if (normalizedCode) {
        result.country = normalizedCode;
      }
    } else {
      (result as Record<string, string>)[contactField] = value;
    }
  });

  // Return null if no valid phone number
  if (!result.phoneNumber || result.phoneNumber.trim().length === 0) {
    return null;
  }

  return result as ImportContactsData['contacts'][0];
};

// ==================== STEP COMPONENTS ====================

// Step 1: Upload
const UploadStep = ({
  file,
  onFileChange,
  skipDuplicates,
  setSkipDuplicates,
  updateExisting,
  setUpdateExisting,
  selectedGroupIds,
  setSelectedGroupIds,
  groups,
  dragOver,
  setDragOver,
}: {
  file: File | null;
  onFileChange: (file: File | null) => void;
  skipDuplicates: boolean;
  setSkipDuplicates: (v: boolean) => void;
  updateExisting: boolean;
  setUpdateExisting: (v: boolean) => void;
  selectedGroupIds: string[];
  setSelectedGroupIds: (ids: string[]) => void;
  groups: ContactGroup[];
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
}) => {
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setDragOver(false);
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile && (droppedFile.type === 'text/csv' || droppedFile.name.endsWith('.csv'))) {
        onFileChange(droppedFile);
      }
    },
    [onFileChange, setDragOver]
  );

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      onFileChange(selectedFile);
    }
  };

  return (
    <div className="space-y-6">
      {/* File Upload Area */}
      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-colors ${
          dragOver
            ? 'border-orange-500 bg-orange-500/10'
            : file
            ? 'border-green-500/50 bg-green-500/5'
            : 'border-gray-600 hover:border-gray-500'
        }`}
      >
        <input
          type="file"
          accept=".csv"
          onChange={handleFileSelect}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        
        {file ? (
          <div className="flex items-center justify-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-green-400" />
            </div>
            <div className="text-left">
              <p className="text-white font-medium">{file.name}</p>
              <p className="text-sm text-gray-400">
                {(file.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onFileChange(null);
              }}
              className="p-2 rounded-lg hover:bg-red-500/20 text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <div className="w-16 h-16 rounded-2xl bg-linear-to-br from-orange-500/20 to-orange-600/10 flex items-center justify-center mx-auto mb-4">
              <Upload className="w-8 h-8 text-orange-400" />
            </div>
            <p className="text-white font-medium mb-1">
              Drop your CSV file here or click to browse
            </p>
            <p className="text-sm text-gray-500">
              Supports .csv files only
            </p>
          </>
        )}
      </div>

      {/* Download Template */}
      <div className="flex items-center justify-center">
        <button
          type="button"
          onClick={() => {
            const template = 'Phone Number,Name,Email,Company,Country,Tags,Notes\n+1234567890,John Doe,john@example.com,Acme Inc,US,"tag1, tag2",Sample notes';
            const blob = new Blob([template], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'contacts-template.csv';
            a.click();
            URL.revokeObjectURL(url);
          }}
          className="text-sm text-orange-400 hover:text-orange-300 flex items-center gap-2 transition-colors"
        >
          <Download className="w-4 h-4" />
          Download CSV Template
        </button>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h4 className="text-sm font-medium text-white">Import Options</h4>
        
        <div className="space-y-3">
          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
            <Checkbox
              checked={skipDuplicates}
              onChange={(e) => setSkipDuplicates(e.target.checked)}
              disabled={true}
            />
            <div>
              <p className="text-sm text-white">Skip duplicates</p>
              <p className="text-xs text-gray-500">Skip contacts that already exist (by phone number)</p>
            </div>
          </label>

          <label className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 transition-colors">
            <Checkbox
              checked={updateExisting}
              onChange={(e) => setUpdateExisting(e.target.checked)}
            />
            <div>
              <p className="text-sm text-white">Update existing contacts</p>
              <p className="text-xs text-gray-500">Update contact details if phone number already exists</p>
            </div>
          </label>
        </div>
      </div>

      {/* Group Selection */}
      {groups.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-white">Add to Groups (optional)</h4>
          <div className="flex flex-wrap gap-2">
            {groups.map((group) => {
              const isSelected = selectedGroupIds.includes(group.id);
              return (
                <button
                  key={group.id}
                  type="button"
                  onClick={() => {
                    setSelectedGroupIds(
                      isSelected
                        ? selectedGroupIds.filter((id) => id !== group.id)
                        : [...selectedGroupIds, group.id]
                    );
                  }}
                  className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                    isSelected
                      ? 'bg-orange-500 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {isSelected && <Check className="w-3 h-3" />}
                  {group.name}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Step 2: Field Mapping
const MappingStep = ({
  mapping,
  setMapping,
  sampleData,
}: {
  mapping: FieldMapping[];
  setMapping: (m: FieldMapping[]) => void;
  sampleData: ParsedContact[];
}) => {
  const handleMappingChange = (csvColumn: string, contactField: string) => {
    // If mapping to a field that's already mapped, unmap the previous one
    const newMapping = mapping.map((m) => {
      if (m.csvColumn === csvColumn) {
        return { ...m, contactField };
      }
      if (m.contactField === contactField && contactField !== '') {
        return { ...m, contactField: '' };
      }
      return m;
    });
    setMapping(newMapping);
  };

  const hasPhoneMapping = mapping.some((m) => m.contactField === 'phoneNumber');

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-white">Map CSV Columns to Contact Fields</h4>
          <p className="text-xs text-gray-500 mt-1">
            Match your CSV columns to the appropriate contact fields
          </p>
        </div>
        {!hasPhoneMapping && (
          <Badge variant="warning" className="flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Phone number required
          </Badge>
        )}
      </div>

      <div className="space-y-3">
        {mapping.map(({ csvColumn, contactField }) => {
          const sampleValue = sampleData[0]?.[csvColumn] || '';
          
          return (
            <div
              key={csvColumn}
              className="flex items-center gap-4 p-3 rounded-lg bg-white/5 border border-white/10"
            >
              {/* CSV Column */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{csvColumn}</p>
                <p className="text-xs text-gray-500 truncate">
                  Sample: {sampleValue || <span className="italic">empty</span>}
                </p>
              </div>

              {/* Arrow */}
              <ArrowRight className="w-4 h-4 text-gray-500 shrink-0" />

              {/* Contact Field Select */}
              <div className="w-48">
                <Select
                  options={CONTACT_FIELDS}
                  value={contactField}
                  onChange={(value) => handleMappingChange(csvColumn, value as string)}
                  placeholder="Select field..."
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Step 3: Preview
const PreviewStep = ({
  contacts,
  mapping,
  validationErrors,
}: {
  contacts: ParsedContact[];
  mapping: FieldMapping[];
  validationErrors: ValidationError[];
}) => {
  const mappedFields = mapping.filter((m) => m.contactField);
  const errorRowNumbers = new Set(validationErrors.map((e) => e.row));
  const validCount = contacts.length - errorRowNumbers.size;

  return (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-white">{contacts.length}</p>
          <p className="text-xs text-gray-500">Total Rows</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-green-400">{validCount}</p>
          <p className="text-xs text-gray-500">Valid</p>
        </Card>
        <Card className="p-4 text-center">
          <p className="text-2xl font-bold text-red-400">{errorRowNumbers.size}</p>
          <p className="text-xs text-gray-500">Errors</p>
        </Card>
      </div>

      {/* Validation Errors */}
      {validationErrors.length > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-sm font-medium text-red-300">
              {validationErrors.length} validation error{validationErrors.length !== 1 ? 's' : ''} found
            </p>
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {validationErrors.slice(0, 10).map((error, index) => (
              <p key={index} className="text-xs text-red-300">
                Row {error.row}: {error.message}
              </p>
            ))}
            {validationErrors.length > 10 && (
              <p className="text-xs text-red-400 font-medium">
                +{validationErrors.length - 10} more errors...
              </p>
            )}
          </div>
        </div>
      )}

      {/* Preview Table */}
      <div className="overflow-x-auto rounded-lg border border-white/10">
        <table className="w-full">
          <thead className="bg-white/5">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                #
              </th>
              {mappedFields.map(({ csvColumn, contactField }) => (
                <th
                  key={`header-${csvColumn}-${contactField}`}
                  className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider"
                >
                  {CONTACT_FIELDS.find((f) => f.value === contactField)?.label || contactField}
                </th>
              ))}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {contacts.slice(0, 10).map((contact, index) => {
              const rowErrors = validationErrors.filter((e) => e.row === index + 1);
              const hasError = rowErrors.length > 0;

              return (
                <tr
                  key={index}
                  className={hasError ? 'bg-red-500/5' : 'hover:bg-white/5'}
                >
                  <td className="px-4 py-3 text-sm text-gray-500">{index + 1}</td>
                  {mappedFields.map(({ csvColumn, contactField }) => (
                    <td
                      key={`cell-${csvColumn}-${contactField}`}
                      className={`px-4 py-3 text-sm ${
                        rowErrors.some((e) => e.field === contactField)
                          ? 'text-red-400'
                          : 'text-white'
                      }`}
                    >
                      {contact[csvColumn] || <span className="text-gray-600">—</span>}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    {hasError ? (
                      <Badge variant="error" className="text-xs">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Error
                      </Badge>
                    ) : (
                      <Badge variant="success" className="text-xs">
                        <Check className="w-3 h-3 mr-1" />
                        Valid
                      </Badge>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {contacts.length > 10 && (
          <div className="px-4 py-3 text-center text-sm text-gray-500 bg-white/5">
            Showing first 10 of {contacts.length} rows
          </div>
        )}
      </div>
    </div>
  );
};

// Step 4: Results
const ResultsStep = ({
  result,
  isLoading,
  error,
}: {
  result: ImportResult | null;
  isLoading: boolean;
  error: string | null;
}) => {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loader2 className="w-12 h-12 text-orange-500 animate-spin mb-4" />
        <p className="text-white font-medium">Importing contacts...</p>
        <p className="text-sm text-gray-500">This may take a moment</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
          <X className="w-8 h-8 text-red-400" />
        </div>
        <p className="text-white font-medium mb-2">Import Failed</p>
        <p className="text-sm text-gray-400 text-center max-w-md">{error}</p>
      </div>
    );
  }

  if (!result) return null;

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="flex flex-col items-center justify-center py-6">
        <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
          <CheckCircle className="w-8 h-8 text-green-400" />
        </div>
        <p className="text-white font-medium text-lg">Import Complete!</p>
      </div>

      {/* Results Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 text-center bg-green-500/10 border-green-500/30">
          <p className="text-2xl font-bold text-green-400">{result.imported}</p>
          <p className="text-xs text-gray-400">Imported</p>
        </Card>
        <Card className="p-4 text-center bg-blue-500/10 border-blue-500/30">
          <p className="text-2xl font-bold text-blue-400">{result.updated}</p>
          <p className="text-xs text-gray-400">Updated</p>
        </Card>
        <Card className="p-4 text-center bg-yellow-500/10 border-yellow-500/30">
          <p className="text-2xl font-bold text-yellow-400">{result.skipped}</p>
          <p className="text-xs text-gray-400">Skipped</p>
        </Card>
      </div>

      {/* Errors */}
      {result.errors && result.errors.length > 0 && (
        <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <p className="text-sm font-medium text-red-300">
              {result.errors.length} error{result.errors.length !== 1 ? 's' : ''} occurred
            </p>
          </div>
          <div className="max-h-32 overflow-y-auto space-y-1">
            {result.errors.map((error, index) => (
              <p key={index} className="text-xs text-red-300">
                Row {error.row} ({error.phoneNumber}): {error.error}
              </p>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== MAIN COMPONENT ====================

const ImportContactsModal = ({ isOpen, onClose, onSuccess, groups }: ImportContactsModalProps) => {
  // State
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [updateExisting, setUpdateExisting] = useState(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  
  // Parsed data
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [mapping, setMapping] = useState<FieldMapping[]>([]);
  
  // Import state
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Validation errors
  const validationErrors = useMemo(() => {
    if (currentStep < 3) return [];
    return parsedContacts.flatMap((contact, index) =>
      validateContact(contact, mapping, index)
    );
  }, [parsedContacts, mapping, currentStep]);

  // Handle file change
  const handleFileChange = useCallback(async (newFile: File | null) => {
    setFile(newFile);
    
    if (newFile) {
      const content = await newFile.text();
      const { headers: csvHeaders, rows } = parseCSV(content);
      setParsedContacts(rows);
      setMapping(autoDetectMapping(csvHeaders));
    } else {
      setParsedContacts([]);
      setMapping([]);
    }
  }, []);

  // Check if can proceed
  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 1:
        return file !== null && parsedContacts.length > 0;
      case 2:
        return mapping.some((m) => m.contactField === 'phoneNumber');
      case 3:
        return validationErrors.length === 0 || 
               validationErrors.length < parsedContacts.length; // Allow if at least some are valid
      default:
        return true;
    }
  }, [currentStep, file, parsedContacts, mapping, validationErrors]);

  // Handle import
  const handleImport = async () => {
    setIsImporting(true);
    setImportError(null);

    try {
      // Filter out invalid contacts (validation errors and null mappings)
      const errorRows = new Set(validationErrors.map((e) => e.row));
      const validContacts = parsedContacts
        .filter((_, index) => !errorRows.has(index + 1))
        .map((contact) => mapContactData(contact, mapping))
        .filter((c): c is NonNullable<typeof c> => c !== null);

      // Check if we have any valid contacts
      if (validContacts.length === 0) {
        setImportError('No valid contacts to import. Please ensure phone numbers are mapped correctly.');
        setCurrentStep(4);
        return;
      }

      console.log('Importing contacts:', validContacts); // Debug log

      const result = await contactsService.import({
        contacts: validContacts,
        skipDuplicates,
        updateExisting,
        groupIds: selectedGroupIds.length > 0 ? selectedGroupIds : undefined,
      });

      setImportResult(result);
      setCurrentStep(4);
    } catch (error) {
      console.error('Import failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to import contacts';
      // Try to extract more specific error from API response
      const apiError = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setImportError(apiError || errorMessage);
      setCurrentStep(4);
    } finally {
      setIsImporting(false);
    }
  };

  // Handle next step
  const handleNext = () => {
    if (currentStep === 3) {
      handleImport();
    } else {
      setCurrentStep((prev) => Math.min(prev + 1, 4));
    }
  };

  // Handle close
  const handleClose = () => {
    if (importResult) {
      onSuccess();
    }
    
    // Reset state
    setCurrentStep(1);
    setFile(null);
    setParsedContacts([]);
    setMapping([]);
    setSkipDuplicates(true);
    setUpdateExisting(false);
    setSelectedGroupIds([]);
    setImportResult(null);
    setImportError(null);
    
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Import Contacts"
      subtitle="Upload a CSV file to import contacts in bulk"
      size="xl"
      showCloseButton
      closeOnBackdropClick={false}
    >
      {/* Progress Steps */}
      <div className="px-6 py-4 border-b border-(--color-border-gray)">
        <div className="flex items-center justify-between">
          {STEPS.map((step, index) => {
            const Icon = step.icon;
            const isActive = currentStep === step.id;
            const isCompleted = currentStep > step.id;
            
            return (
              <div key={step.id} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                      isCompleted
                        ? 'bg-green-500 text-white'
                        : isActive
                        ? 'bg-orange-500 text-white'
                        : 'bg-white/10 text-gray-400'
                    }`}
                  >
                    {isCompleted ? <Check className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                  </div>
                  <p
                    className={`text-xs mt-2 ${
                      isActive ? 'text-orange-400 font-medium' : 'text-gray-500'
                    }`}
                  >
                    {step.label}
                  </p>
                </div>
                
                {index < STEPS.length - 1 && (
                  <div
                    className={`w-16 h-0.5 mx-2 ${
                      currentStep > step.id ? 'bg-green-500' : 'bg-white/10'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      <div className="p-6 max-h-[60vh] overflow-y-auto">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStep}
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.2 }}
          >
            {currentStep === 1 && (
              <UploadStep
                file={file}
                onFileChange={handleFileChange}
                skipDuplicates={skipDuplicates}
                setSkipDuplicates={setSkipDuplicates}
                updateExisting={updateExisting}
                setUpdateExisting={setUpdateExisting}
                selectedGroupIds={selectedGroupIds}
                setSelectedGroupIds={setSelectedGroupIds}
                groups={groups}
                dragOver={dragOver}
                setDragOver={setDragOver}
              />
            )}

            {currentStep === 2 && (
              <MappingStep
                mapping={mapping}
                setMapping={setMapping}
                sampleData={parsedContacts.slice(0, 3)}
              />
            )}

            {currentStep === 3 && (
              <PreviewStep
                contacts={parsedContacts}
                mapping={mapping}
                validationErrors={validationErrors}
              />
            )}

            {currentStep === 4 && (
              <ResultsStep
                result={importResult}
                isLoading={isImporting}
                error={importError}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-(--color-border-gray) flex items-center justify-between">
        <div>
          {currentStep > 1 && currentStep < 4 && (
            <Button
              variant="ghost"
              onClick={() => setCurrentStep((prev) => Math.max(prev - 1, 1))}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          )}
        </div>

        <div className="flex items-center gap-3">
          {currentStep < 4 ? (
            <>
              <Button variant="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleNext} disabled={!canProceed || isImporting}>
                {isImporting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Importing...
                  </>
                ) : currentStep === 3 ? (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import Contacts
                  </>
                ) : (
                  <>
                    Next
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </>
                )}
              </Button>
            </>
          ) : (
            <Button onClick={handleClose}>
              {importResult ? 'Done' : 'Close'}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ImportContactsModal;

